import type { DataSource, ListQuery } from "@estelle/domain";

import {
  RECENT_DEFAULT_SORT,
  RECENT_FIELDS,
  SEARCH_FIELDS,
} from "@/modules/docs/fields";
import { runLocalQuery } from "@/modules/docs/localQuery";
import { projectDocRow, type Projection } from "@/modules/docs/projections";
import { Browse } from "@/modules/docs/screens/Browse";
import { Deleted } from "@/modules/docs/screens/Deleted";
import { Permissions } from "@/modules/docs/screens/Permissions";
import { Recent } from "@/modules/docs/screens/Recent";
import { Search } from "@/modules/docs/screens/Search";
import type { DocRow } from "@/modules/docs/types";
import { fetchDocsTree } from "@/modules/docs/useDocsTree";
import type { ModuleUi } from "@/modules/types";

/**
 * Recent's rows, filtered and projected for export.
 *
 * Not `collectAll` (`components/table/export.ts`): that helper follows a
 * repository's own cursor to the end, and Recent has no repository call to
 * follow — its rows are the same client-side walk `fetchDocsTree` does for
 * the screen itself (see that file's comment for why `DocsRepository` has
 * no single method for this). So this repeats the walk and reuses the same
 * local filter engine the screen renders through, rather than exporting
 * whatever the table happens to have on screen.
 */
async function exportRecentRows(
  data: DataSource,
  query: ListQuery,
): Promise<Projection[]> {
  const tree = await fetchDocsTree(data);
  const usersPage = await data.settings.listUsers({ limit: 200 });
  const nameByUserId = new Map(
    usersPage.items.map((user) => [user.id, user.name]),
  );

  const rows: DocRow[] = tree.allPages.map((page) => {
    const content = tree.contentByPageId.get(page.id);
    const authorId = content?.revision.authorUserId;
    return {
      page,
      folder: tree.folders.find((folder) => folder.id === page.folderId),
      content,
      authorName: authorId ? (nameByUserId.get(authorId) ?? authorId) : "",
    };
  });

  const result = runLocalQuery(
    rows,
    {
      filter: query.filter ?? {
        kind: "group",
        combinator: "and",
        children: [],
      },
      sort: query.sort ?? [],
      limit: rows.length + 1,
    },
    {
      project: projectDocRow,
      identify: (row) => row.page.id,
      // The screen's own default: the list is newest-first until someone sorts.
      defaultSort: RECENT_DEFAULT_SORT,
    },
  );

  return result.items.map(projectDocRow);
}

/**
 * The Docs module (§7.8: promoted to phase 1 on 2026-09-09, screens against
 * fixtures, backend stays phase 2).
 *
 * `browse`, `permissions` and `deleted` declare no fields — a folder tree, a
 * grants editor and a folder's recovery list are not filterable lists, the
 * same reason `ContactTypes` declares none. `recent` and `search` are, and
 * both run through the same filter bar every other list in the application
 * does (`localQuery.ts`), because CLAUDE.md is explicit that a module
 * shipping its own filter UI is a review rejection.
 */
export const docsModule: ModuleUi = {
  id: "docs",
  sections: {
    browse: { fields: [], screen: Browse, padded: true },
    recent: {
      fields: RECENT_FIELDS,
      screen: Recent,
      exportRows: exportRecentRows,
      nothingYet: { title: "No pages edited yet" },
    },
    search: { fields: SEARCH_FIELDS, screen: Search },
    permissions: { fields: [], screen: Permissions },
    deleted: { fields: [], screen: Deleted },
  },
};
